import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import Button from './Button';
import Card from './Card';
import TextField from './TextField';
import {
  FileUpload as FileUploadIcon,
  Code as CodeIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const LessonJsonImport = ({ moduleTitle, onSuccess }) => {
  const { t } = useTranslation(['courses', 'common']);
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStructure, setShowStructure] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setJsonText('');
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setShowStructure(false);
  };

  const handleImport = async () => {
    try {
      if (!jsonText.trim()) {
        setError(t('courses:lessonImport.validation.emptyJson'));
        return;
      }
      let parsedData;
      try {
        parsedData = JSON.parse(jsonText);
      } catch (err) {
        setError(t('courses:lessonImport.validation.invalidJson') + err.message);
        return;
      }
      if (!parsedData.title) {
        setError(t('courses:lessonImport.validation.missingTitle'));
        return;
      }
      setLoading(true);
      // --- Импорт всегда только локально, без запросов к серверу ---
      const lessonWithTempId = {
        ...parsedData,
      };
      if (onSuccess) {
        onSuccess(lessonWithTempId);
        toast.success(t('courses:lessonImport.success.imported'));
        handleClose();
      }
      setLoading(false);
    } catch (error) {
      if (error.message?.includes('JSON')) {
        setError(t('courses:lessonImport.validation.invalidJson') + error.message);
      } else {
        setError(
          t('courses:lessonImport.validation.importError') +
            (error.message || t('courses:lessonImport.validation.unknownError'))
        );
      }
      setLoading(false);
    }
  };

  const toggleStructure = () => {
    setShowStructure(!showStructure);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        setJsonText(content);
        JSON.parse(content);
        setError('');
      } catch (err) {
        setError(t('courses:lessonImport.validation.invalidJson') + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Структура JSON урока для помощи пользователю
  const jsonStructure = {
    title: t('courses:lessonImport.example.title'),
    content: t('courses:lessonImport.example.content'),
    duration: t('courses:lessonImport.example.duration'),
    type: t('courses:lessonImport.example.type'),
    contentType: t('courses:lessonImport.example.contentType'),
    videoUrl: t('courses:lessonImport.example.videoUrl'),
    contentBlocks: [
      {
        type: t('courses:lessonImport.example.blockType'),
        content: t('courses:lessonImport.example.blockContent'),
        order: t('courses:lessonImport.example.blockOrder'),
      },
    ],
    resources: [
      {
        title: t('courses:lessonImport.example.resourceTitle'),
        type: t('courses:lessonImport.example.resourceType'),
        description: t('courses:lessonImport.example.resourceDescription'),
        url: t('courses:lessonImport.example.resourceUrl'),
      },
    ],
    practiceExercises: [
      {
        title: t('courses:lessonImport.example.exerciseTitle'),
        description: t('courses:lessonImport.example.exerciseDescription'),
        codeSnippet: t('courses:lessonImport.example.codeSnippet'),
        playgroundUrl: t('courses:lessonImport.example.playgroundUrl'),
      },
    ],
  };
  
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        sx={{ 
          ml: 1,
          fontSize: '0.75rem',
          minWidth: '122px',
          minHeight: '35px',
          lineHeight: 1,
          py: 0.5,
          px: 1.5,
          borderColor: 'button.secondaryBorder',
          color: 'button.secondaryText',
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: 'button.secondaryBorder',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
      >
        {t('courses:lessonImport.button.import')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CodeIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:lessonImport.moduleTitle', { moduleTitle })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('courses:lessonImport.subtitle')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Button
              variant="contained"
              component="label"
              startIcon={<FileUploadIcon />}
              size="medium"
            >
              {t('courses:lessonImport.button.upload')}
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleUpload}
              />
            </Button>
            <Tooltip title={t('courses:lessonImport.button.showStructure')}>
              <IconButton onClick={toggleStructure}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            multiline
            fullWidth
            rows={12}
            value={jsonText}
            onChange={(e) => {
              if (error) setError('');
              setJsonText(e.target.value);
            }}
            variant="outlined"
            error={!!error}
            placeholder={t('courses:lessonImport.placeholder')}
            sx={{ mb: 1 }}
          />
          {showStructure && (
            <Card sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('courses:lessonImport.structureExample')}
              </Typography>
              <TextField
                multiline
                fullWidth
                rows={10}
                value={JSON.stringify(jsonStructure, null, 2)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                  },
                }}
              />
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined" size="large">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            size="large"
            disabled={!jsonText.trim() || !!error || loading}
          >
            {loading
              ? t('courses:lessonImport.button.importing')
              : t('courses:lessonImport.button.importAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LessonJsonImport;
